import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronRight, ChevronLeft, Check, Baby, FileText, Users, Settings, BarChart3, Sparkles } from 'lucide-react'

/**
 * OnboardingTour - Tour guidato per nuovi utenti
 * Mostra step-by-step le funzionalità principali
 * 
 * Fix v2 (29/12/2024):
 * - Rimosso backdrop-blur che copriva gli elementi evidenziati
 * - Corretto posizionamento tooltip su mobile
 * - Aggiunto rilevamento automatico mobile/desktop
 * - Tooltip sempre visibile dentro lo schermo
 * - Spotlight con "buco" trasparente reale
 */

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Benvenuto in Il Diario di Bordo! 🎉',
    description: 'Facciamo un breve tour delle funzionalità principali. Ci vorranno solo 2 minuti!',
    icon: Sparkles,
    position: 'center',
    target: null
  },
  {
    id: 'minori',
    title: 'Gestione Minori',
    description: 'Qui trovi tutti i minori del centro. Puoi aggiungere nuovi minori, vedere i dettagli e assegnare educatori referenti.',
    icon: Baby,
    position: 'auto', // Automatico: right su desktop, top su mobile
    target: '[href="/minori"]',
    highlight: true
  },
  {
    id: 'entries',
    title: 'Registrazioni Giornaliere',
    description: 'La sezione più importante! Ogni giorno compili la registrazione per ogni minore presente. Il pulsante "+" in basso ti permette di crearne una nuova velocemente.',
    icon: FileText,
    position: 'auto',
    target: '[href="/entries"]',
    highlight: true
  },
  {
    id: 'new-entry',
    title: 'Nuova Registrazione',
    description: 'Tocca il pulsante centrale per creare una nuova registrazione. È il modo più veloce!',
    icon: FileText,
    position: 'top',
    target: '.bottom-nav-item-center, [href="/entries/new"]',
    highlight: true,
    mobileOnly: true
  },
  {
    id: 'reports',
    title: 'Report e Statistiche',
    description: 'Qui puoi generare report dettagliati, esportare dati in Excel/PDF e vedere le statistiche del centro.',
    icon: BarChart3,
    position: 'auto',
    target: '[href="/reports"]',
    highlight: true
  },
  {
    id: 'users',
    title: 'Gestione Utenti',
    description: 'Solo per coordinatori: invita nuovi educatori, gestisci i permessi e monitora gli accessi.',
    icon: Users,
    position: 'auto',
    target: '[href="/users"]',
    highlight: true,
    coordinatoreOnly: true
  },
  {
    id: 'settings',
    title: 'Impostazioni',
    description: 'Personalizza il centro: logo, campi del form, backup automatici su Google Drive e molto altro.',
    icon: Settings,
    position: 'auto',
    target: '[href="/settings"]',
    highlight: true,
    coordinatoreOnly: true
  },
  {
    id: 'complete',
    title: 'Tutto pronto! 🚀',
    description: 'Ora sei pronto per iniziare. Se hai domande, usa il tasto "?" o contattaci. Buon lavoro!',
    icon: Check,
    position: 'center',
    target: null
  }
]

export default function OnboardingTour({ isOpen, onComplete, isCoordinatore = false }) {
  const [currentStep, setCurrentStep] = useState(0)
  const [tooltipStyle, setTooltipStyle] = useState({})
  const [spotlightRect, setSpotlightRect] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const tooltipRef = useRef(null)

  // Rileva mobile/desktop
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Filtra steps in base al ruolo e device
  const steps = TOUR_STEPS.filter(step => {
    if (step.coordinatoreOnly && !isCoordinatore) return false
    if (step.mobileOnly && !isMobile) return false
    return true
  })

  const step = steps[currentStep]
  const isFirst = currentStep === 0
  const isLast = currentStep === steps.length - 1
  const progress = ((currentStep + 1) / steps.length) * 100

  // Posiziona tooltip e spotlight
  useLayoutEffect(() => {
    if (!isOpen) return

    // Step centrato (welcome/complete)
    if (!step?.target || step.position === 'center') {
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      })
      setSpotlightRect(null)
      return
    }

    // Trova elemento target (supporta selettori multipli separati da virgola)
    const selectors = step.target.split(',').map(s => s.trim())
    let target = null
    for (const selector of selectors) {
      target = document.querySelector(selector)
      if (target) break
    }

    if (!target) {
      // Fallback: centra se target non trovato
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      })
      setSpotlightRect(null)
      return
    }

    // Scroll to target
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // Attendi un frame per il rendering
    requestAnimationFrame(() => {
      const rect = target.getBoundingClientRect()
      const tooltipEl = tooltipRef.current
      const tooltipWidth = tooltipEl?.offsetWidth || 320
      const tooltipHeight = tooltipEl?.offsetHeight || 250
      const padding = 16
      const arrowOffset = 12

      // Imposta spotlight
      setSpotlightRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16
      })

      // Determina posizione ottimale
      let position = step.position
      if (position === 'auto') {
        position = isMobile ? 'top' : 'right'
      }

      // Calcola posizione tooltip
      let top, left, transform = ''

      switch (position) {
        case 'right':
          top = rect.top + rect.height / 2 - tooltipHeight / 2
          left = rect.right + arrowOffset
          // Se esce a destra, metti a sinistra
          if (left + tooltipWidth > window.innerWidth - padding) {
            left = rect.left - tooltipWidth - arrowOffset
          }
          break

        case 'left':
          top = rect.top + rect.height / 2 - tooltipHeight / 2
          left = rect.left - tooltipWidth - arrowOffset
          // Se esce a sinistra, metti a destra
          if (left < padding) {
            left = rect.right + arrowOffset
          }
          break

        case 'top':
          top = rect.top - tooltipHeight - arrowOffset
          left = rect.left + rect.width / 2 - tooltipWidth / 2
          // Se esce sopra, metti sotto
          if (top < padding) {
            top = rect.bottom + arrowOffset
          }
          break

        case 'bottom':
          top = rect.bottom + arrowOffset
          left = rect.left + rect.width / 2 - tooltipWidth / 2
          // Se esce sotto, metti sopra
          if (top + tooltipHeight > window.innerHeight - padding) {
            top = rect.top - tooltipHeight - arrowOffset
          }
          break

        default:
          top = window.innerHeight / 2 - tooltipHeight / 2
          left = window.innerWidth / 2 - tooltipWidth / 2
      }

      // Clamp dentro lo schermo
      top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding))
      left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding))

      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`
      })
    })
  }, [currentStep, step, isOpen, isMobile])

  const handleNext = () => {
    if (isLast) {
      onComplete()
    } else {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(0, prev - 1))
  }

  const handleSkip = () => {
    onComplete()
  }

  if (!isOpen) return null

  const Icon = step.icon

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      {/* Overlay scuro SENZA blur */}
      <div 
        className="absolute inset-0 bg-black/70 transition-opacity duration-300" 
        onClick={handleSkip}
      />

      {/* Spotlight - buco trasparente nell'overlay */}
      {spotlightRect && step.highlight && (
        <div
          className="absolute z-[100] rounded-xl transition-all duration-300 ease-out"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
            border: '3px solid rgb(14, 165, 233)', // primary-500
            background: 'transparent'
          }}
        >
          {/* Ring animato */}
          <div className="absolute -inset-1 rounded-xl border-2 border-primary-400 animate-ping opacity-75" />
        </div>
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="z-[101] w-80 max-w-[calc(100vw-2rem)]"
        style={tooltipStyle}
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
          {/* Progress bar */}
          <div className="h-1.5 bg-gray-100 dark:bg-gray-700">
            <div 
              className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-5">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/50 shrink-0">
                <Icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>

            {/* Step indicator */}
            <div className="flex items-center justify-center gap-1.5 mb-4">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx === currentStep 
                      ? 'w-6 bg-primary-500' 
                      : idx < currentStep 
                        ? 'w-1.5 bg-primary-300 dark:bg-primary-600' 
                        : 'w-1.5 bg-gray-200 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                Salta tour
              </button>

              <div className="flex items-center gap-2">
                {!isFirst && (
                  <button
                    onClick={handlePrev}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Indietro</span>
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm"
                >
                  {isLast ? 'Inizia!' : 'Avanti'}
                  {!isLast && <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={handleSkip}
        className="fixed top-4 right-4 z-[102] p-2.5 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Chiudi tutorial"
      >
        <X className="h-5 w-5 text-white" />
      </button>
    </div>,
    document.body
  )
}

// Hook per gestire lo stato del tour
export function useOnboardingTour(storageKey = 'onboarding_completed') {
  const [showTour, setShowTour] = useState(false)
  const [hasCompleted, setHasCompleted] = useState(true)

  useEffect(() => {
    const completed = localStorage.getItem(storageKey)
    if (!completed) {
      setHasCompleted(false)
      // Mostra tour dopo un breve delay per permettere caricamento UI
      setTimeout(() => setShowTour(true), 1000)
    }
  }, [storageKey])

  const completeTour = () => {
    localStorage.setItem(storageKey, 'true')
    setHasCompleted(true)
    setShowTour(false)
  }

  const resetTour = () => {
    localStorage.removeItem(storageKey)
    setHasCompleted(false)
    setShowTour(true)
  }

  return {
    showTour,
    hasCompleted,
    completeTour,
    resetTour,
    startTour: () => setShowTour(true)
  }
}

// CSS da aggiungere al file index.css o globals.css
export const onboardingStyles = `
@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-scale-in {
  animation: scale-in 0.25s ease-out;
}
`
